<!--
	@component
	Shared catalog + material autocomplete pair where material depends on the selected catalog.
	Store-agnostic: bind `catalog` and `material` directly. Consumed by the lot form
	(`LotMaterialForm`) and the minilot match form (`Minilot`).

	## Behaviour
	- selecting a catalog auto-selects that catalog's first preloaded material
	- material search is scoped to the current catalog (reactive fetch URL)
	- clearing the catalog clears + disables material and tears down its dropdown
	- a saved material outside the catalog defaults is kept visible (prefill)

	@see {@link ./catalogMaterial.ts} for the option-derivation logic
	@example
	```svelte
	<CatalogMaterialAutocomplete
		bind:catalog={lot.catalog}
		bind:material={lot.material}
		catalogOptions={defaultCatalogOptions}
		materialDefaultsByCatalog={defaultMaterialOptions}
		onCatalogChange={save}
	/>
	```
-->
<script lang="ts">
	import { t } from '$i18n/i18n'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import type { Catalog } from '#lib/map/types/catalog.js'
	import type { Material } from '#lib/map/types/material.js'
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import { getFormAcConfigFetch } from '$types/autocompleteFieldConfig'
	import {
		firstMaterialForCatalog,
		materialFetchUrl,
		materialOptionsForCatalog
	} from './catalogMaterial'
	import { tick } from 'svelte'

	type Props = {
		catalog: Catalog | null
		material: Material | null
		/** preloaded catalog defaults */
		catalogOptions: Catalog[]
		/** preloaded material defaults keyed by catalog id (materialDefaultsMap) */
		materialDefaultsByCatalog: Record<string, Material[]>
		/** read-only mode (lot not editable) */
		disabled?: boolean
		/** autosave in flight — disables both fields to prevent the echo-clobber race */
		saving?: boolean
		fontProps?: FontProps
		/** form-error path prefix: '' for minilot, 'lot.' for the lot form */
		errorPathPrefix?: string
		/** show the svelecte dropdown caret (minilot shows it; the lot form hides it) */
		hideDropdownArrow?: boolean
		catalogGridArea?: string
		materialGridArea?: string
		catalogInputId?: string
		materialInputId?: string
		/** fired after the bound catalog value changes (consumer persists / resets extras) */
		onCatalogChange?: () => void
		onMaterialChange?: () => void
		onCatalogClear?: () => void
		onMaterialClear?: () => void
	}

	let {
		catalog = $bindable(),
		material = $bindable(),
		catalogOptions,
		materialDefaultsByCatalog,
		disabled = false,
		saving = false,
		fontProps = DefaultRobotoFontProps('0.875rem', '0.75rem'),
		errorPathPrefix = '',
		hideDropdownArrow = true,
		catalogGridArea,
		materialGridArea,
		catalogInputId = 'lotCatalog',
		materialInputId = 'lotMaterial',
		onCatalogChange,
		onMaterialChange,
		onCatalogClear,
		onMaterialClear
	}: Props = $props()

	// catalog fetch depends on nothing — build once
	const catalogAcConfig = $derived({
		...getFormAcConfigFetch('catalog', false, 'id', 'text', false, false),
		valueAsObject: true,
		fetch:
			catalog?.id && !catalogOptions ? `api/catalog/autocomplete/svelte?qry=[query]` : undefined
	})

	// material options for the selected catalog, incl. prefill of a saved material (see catalogMaterial.ts)
	const materialOptions = $derived(
		materialOptionsForCatalog(catalog, materialDefaultsByCatalog, material)
	)

	/**
	 * reactive fetch config: catalog-scoped material URL via {@link materialFetchUrl} (loading-gate /
	 * always-fetch-when-catalog-selected). $derived is safe here only because onChange never mutates
	 * a dependency of this config — see docs/SVELECTE.md "$derived configs".
	 */
	const materialAcConfig = $derived({
		...getFormAcConfigFetch('material', false, 'id', 'text', false, false),
		valueAsObject: true,
		fetch: materialFetchUrl(catalog, materialDefaultsByCatalog)
	})

	const catalogDisabled = $derived(disabled || saving)
	const materialDisabled = $derived(disabled || saving || !catalog)

	/**
	 * preset the newly selected catalog's first material. uses the catalog svelecte just emitted —
	 * NOT the bound `catalog`, which lags by one selection here (the bind hasn't propagated through
	 * the wrapper when onChange fires), which otherwise auto-selects the PREVIOUS catalog's material.
	 */
	function handleCatalogChange(selectedCatalog: Catalog | null) {
		material = firstMaterialForCatalog(selectedCatalog, materialDefaultsByCatalog)
		onCatalogChange?.()
	}

	async function handleCatalogClear() {
		material = null
		await tick()
		onCatalogClear?.()
	}
</script>

<div style={catalogGridArea ? `grid-area: ${catalogGridArea}` : undefined}>
	<AutocompleteInputField
		id={catalogInputId}
		bind:value={catalog}
		options={catalogOptions}
		disabled={catalogDisabled}
		{fontProps}
		autocompleteConfig={catalogAcConfig}
		label={$t('UI.catalog.autocomplete.ph')}
		errorPath={errorPathPrefix + 'catalog'}
		{hideDropdownArrow}
		required
		onChange={(selected) => handleCatalogChange(selected as Catalog | null)}
		onClear={handleCatalogClear}
	/>
</div>
<div style={materialGridArea ? `grid-area: ${materialGridArea}` : undefined}>
	<!--
		remount the material field only when the catalog appears/disappears (keyed on presence, not
		id). dropping the catalog mid-open otherwise leaves a stale "Keine Optionen" dropdown that
		svelecte keeps rendered; the remount tears it down. switching between two catalogs keeps the
		field mounted so its auto-selected value survives.
	-->
	{#key !catalog}
		<AutocompleteInputField
			id={materialInputId}
			bind:value={material}
			options={materialOptions}
			disabled={materialDisabled}
			{fontProps}
			autocompleteConfig={materialAcConfig}
			label={$t('UI.material.autocomplete.ph')}
			errorPath={errorPathPrefix + 'material'}
			{hideDropdownArrow}
			required
			onChange={() => onMaterialChange?.()}
			onClear={() => {
				material = null
				onMaterialClear?.()
			}}
		/>
	{/key}
</div>
